/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {ColumnState} from 'ag-grid-community';
import produce from 'immer';
import {error} from '@ion/core/src/logger/logger';
import {
  createNotificationType,
  deleteNotificationType,
  deleteNotificationTypeTriggers,
  getSingleNotificationType,
  getTriggerTypes,
  loadNotificationType,
  triggerNotificationType,
  updateNotificationType,
  updateTriggerNotificationType
} from '@ion/core/src/services/Connect';
import {UserContext} from '@ion/core/src/services/MicroServices';
import {
  AuditChange,
  Entity,
  NotificationType,
  NotificationTypeFilters
} from '@ion/core/src/types/entities';
import {LoadNotificationsParams, ServiceError} from '@ion/core/src/types/services';
import {AuditRecordType} from '@ion/dls/src/editors/audit/AuditGridUtils';
import {createModularSearchFilters} from '@ion/features/src/search/utils';
import {
  AdminAction,
  AsyncState,
  PageInitializeAction
} from '@/types/redux';
import {
  getAccountId,
  getGridColumnState,
  onSetGridColumns,
  onUpdateGridColumns
} from '../../../helpers';
import {
  errorAddOptions,
  successAddOptions,
  successEditOptions
} from '../../../prompts/constants';
import {toggleUserPrompt} from '../../../store/actions/user-prompt';
import {PageState} from '../../../types/pages';
import {gridDefaultPageSize} from '../../../utils/helpers';

// name of the reducer
export const NAME                                        = 'notificationType';

/**
 * ACTIONS
 */
export const SET_NOTIFICATION_TYPE_PARAMS                = 'SET_NOTIFICATION_TYPE_PARAMS';

// initialize notificationType ...
export const INITIALIZE_NOTIFICATION_TYPE                = 'INITIALIZE_NOTIFICATION_TYPE';

// grid column state
export const SET_GRID_COLUMNS                            = 'SET_NOTIFICATION_TYPE_GRID_COLUMNS';
export const UPDATE_GRID_COLUMNS                         = 'UPDATE_NOTIFICATION_TYPE_GRID_COLUMNS';

export const NOTIFICATION_TYPE_GRID_REFRESHED            = 'NOTIFICATION_TYPE_GRID_REFRESHED';

export const SET_GRID_PAGE_SIZE                          = 'SET_NOTIFICATION_TYPE_GRID_PAGE_SIZE';

// notification type retrieval ...
export const GET_NOTIFICATION_TYPE_REQUEST               = 'GET_NOTIFICATION_TYPE_REQUEST';
export const GET_NOTIFICATION_TYPE_RESPONSE              = 'GET_NOTIFICATION_TYPE_RESPONSE';
export const GET_NOTIFICATION_TYPE_ERROR                 = 'GET_NOTIFICATION_TYPE_ERROR';

// notification type add ...
export const ADD_NOTIFICATION_TYPE                       = 'ADD_NOTIFICATION_TYPE';
export const ADD_NOTIFICATION_TYPE_REQUEST               = 'ADD_NOTIFICATION_TYPE_REQUEST';
export const ADD_NOTIFICATION_TYPE_RESPONSE              = 'ADD_NOTIFICATION_TYPE_RESPONSE';
export const ADD_NOTIFICATION_TYPE_ERROR                 = 'ADD_NOTIFICATION_TYPE_ERROR';

// notification type edit ...
export const EDIT_NOTIFICATION_TYPE                      = 'EDIT_NOTIFICATION_TYPE';
export const EDIT_NOTIFICATION_TYPE_REQUEST              = 'EDIT_NOTIFICATION_TYPE_REQUEST';
export const EDIT_NOTIFICATION_TYPE_RESPONSE             = 'EDIT_NOTIFICATION_TYPE_RESPONSE';
export const EDIT_NOTIFICATION_TYPE_ERROR                = 'EDIT_NOTIFICATION_TYPE_ERROR';

// notification type delete ...
export const DELETE_NOTIFICATION_TYPE                    = 'DELETE_NOTIFICATION_TYPE';
export const DELETE_NOTIFICATION_TYPE_REQUEST            = 'DELETE_NOTIFICATION_TYPE_REQUEST';
export const DELETE_NOTIFICATION_TYPE_RESPONSE           = 'DELETE_NOTIFICATION_TYPE_RESPONSE';
export const DELETE_NOTIFICATION_TYPE_ERROR              = 'DELETE_NOTIFICATION_TYPE_ERROR';

// Audit notification type
export const AUDIT_NOTIFICATION_TYPE                     = 'AUDIT_NOTIFICATION_TYPE';
export const AUDIT_NOTIFICATION_TYPE_SHOW_CHANGES_DIALOG = 'AUDIT_NOTIFICATION_TYPE_SHOW_CHANGES_DIALOG';

// search queries
export const UPDATE_NOTIFICATION_TYPE_SEARCH_QUERIES     = 'UPDATE_NOTIFICATION_TYPE_SEARCH_QUERIES';
export const RESET_NOTIFICATION_TYPE_SEARCH_QUERIES      = 'RESET_NOTIFICATION_TYPE_SEARCH_QUERIES';

/**
 * action that sets the pathname and query in the store
 */
export const setNotificationTypeParams = ({pathname}) => ({
  type : SET_NOTIFICATION_TYPE_PARAMS, pathname
});

/**
 * @initialize
 */
export const initializeNotificationType = () => ({
  type : INITIALIZE_NOTIFICATION_TYPE
});

/**
 * @NotificationTypeGridRefreshed
 */
export const notificationTypeGridRefreshed = (): PageInitializeAction => {
  return {type: NOTIFICATION_TYPE_GRID_REFRESHED};
};

// Updates the modular search queries for Admin AEMPConfig
export const updateNotificationTypeSearchQueries = (searchParams: NotificationTypeFilters) => ({
  type : UPDATE_NOTIFICATION_TYPE_SEARCH_QUERIES, searchParams
});

// Resets the Peg Labels filters state
export const resetNotificationTypeSearchQueries = () => ({
  type : RESET_NOTIFICATION_TYPE_SEARCH_QUERIES
});

/**
 * Invoked when the the Notification Type auditor is to be displayed or removed.
 */
export const auditNotificationType = (notificationType: NotificationType, show = true) => ({
  type : AUDIT_NOTIFICATION_TYPE, notificationType, show
});

export const showAuditNotificationTypeChangesDialog = (auditRecord: AuditChange, show = true) => ({
  type : AUDIT_NOTIFICATION_TYPE_SHOW_CHANGES_DIALOG, auditRecord, show
});

/**
 * @getNotificationType
 */
export const getNotificationType = ({
  pageId = 1,
  pageSize = 20,
  sort,
  filter,
  callback: callbackFn
}) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    dispatch({type: GET_NOTIFICATION_TYPE_REQUEST});
    const {
      notificationType: {
        search
      }
    } = getState();
    const data: LoadNotificationsParams = {
      accountId : getAccountId(getState),
      pageId,
      pageSize
    };
    if (sort) {
      data.sortOn = sort;
    }
    if (filter) {
      data.filterOn = filter;
    }
    data.filterOn = {
      ...data.filterOn,
      ...createModularSearchFilters(search)
    };
    try {
      const {notificationType: items, totalCount} = await loadNotificationType(data);
      dispatch({type: GET_NOTIFICATION_TYPE_RESPONSE});
      callbackFn?.({items, totalCount});
      return {items, totalCount};
    } catch (err) {
      error(err);
      callbackFn({error: err, items: [], totalCount: 0});
      dispatch({type: GET_NOTIFICATION_TYPE_ERROR, error: err});
      return err;
    }
  };
};

/**
 * Invoked when the Add Admin Notification Type editor is to be displayed or removed.
 */
export const cancelNotificationType = (show = true) => ({
  type : ADD_NOTIFICATION_TYPE, show
});

/**
 * Invoked when a new notification type is to be created; consolidating the process of notification
 * type creation into a single transaction. notification type  creation is proxied through the
 * connect which manage sending out the new user confirmation emails,
 * etc.
 *
 */
export const addNotificationType = (notificationType: NotificationType, context?: UserContext): Function => {
  return async(dispatch: Function): Promise<void> => {
    dispatch({type: ADD_NOTIFICATION_TYPE_REQUEST, notificationType});
    try {
      const actionTemplates = Object.keys(notificationType?.actionTemplates).map((key) => {
        if (notificationType.actionTemplates[key].value !== '') {
          const {href, rel, title} = notificationType.actionTemplates[key]?._self;
          return {href, rel, title};
        }
      });
      const {name, inheritable, description, account, triggerConjunction, triggerNotification} = notificationType;
      const requestparam={
        actionTemplates : actionTemplates.filter((val) => val !== undefined),
        name,
        inheritable,
        description,
        account,
        triggerConjunction
      };
      const alertType = await createNotificationType(requestparam, context);
      await Promise.all(triggerNotification?.map(async(triggerOb, index) => {
        const {attribute, operator, placeHolder}=triggerOb;
        const attributeName = attribute.placeHolder;
        const triggerReq = {
          alertType    : alertType[0]?._self,
          name         : `${name+'_Trigger_'+index}`,
          placeHolders : {
            [attributeName] : placeHolder.value
          },
          triggerExpression : `${attribute.name} ${operator.value} ${'${'+attributeName+'}'}`
        };
        await triggerNotificationType(triggerReq, context);
      }));
      dispatch({type: ADD_NOTIFICATION_TYPE_RESPONSE, notificationType: alertType});
      dispatch(toggleUserPrompt(successAddOptions(Entity.notificationType)));
    } catch (err) {
      dispatch({type: ADD_NOTIFICATION_TYPE_ERROR, error: err});
      dispatch(toggleUserPrompt(errorAddOptions(Entity.notificationType, err, true)));
      error(err);
    }
  };
};

/**
 *
 * Invoked when the Edit Notification types is to be displayed or removed. If the Notification
 * types being edited is the notification types
 */
export const openEdit = (notificationType: NotificationType, show = true, isView) => {
  return async(dispatch: Function): Promise<void> => {
    dispatch({type: EDIT_NOTIFICATION_TYPE, notificationType, show, isView});
  };
};

/**
 *
 * Invoked when an existing Notification Types is to be updated; consolidating the process of
 * Notification Types updates into a single transaction. The process for editing a Notification Types
 *
 */
export const editNotificationType = (
  notificationType: NotificationType,
  context?: UserContext,
  isError?: boolean
): Function => {
  return async(dispatch: Function): Promise<void> => {
    dispatch({type: EDIT_NOTIFICATION_TYPE_REQUEST, notificationType});
    try {
      const actionTemplates = Object.keys(notificationType?.actionTemplates).map((key) => {
        const actionValue = notificationType.actionTemplates[key]?.value;
        if (actionValue !== '') {
          const actionFilter  = notificationType?.actionTemplatesList?.filter(
            (item) => {
              if (actionValue === item.name) {
                return item;
              }
            });
          if (actionFilter[0]?._self) {
            const {href, rel, title} = actionFilter[0]?._self;
            return {href, rel, title};
          }
        }
      });
      const {
        name,
        inheritable,
        description,
        account,
        status,
        triggerNotification,
        triggerConjunction,
        createdOn,
        triggers,
        _self,
        groups,
        version,
        deletedOn,
        id,
        rawLastModifiedOn,
        controlsObj
      } = notificationType;
      const requestparam={
        actionTemplates : actionTemplates.filter((val) => val !== undefined),
        name,
        inheritable,
        description,
        account,
        status,
        triggerConjunction,
        createdOn,
        triggers,
        _self,
        groups,
        version,
        deletedOn,
        id,
        lastModifiedOn  : rawLastModifiedOn,
        controlsObj
      };
      const reloadAlertType = await getSingleNotificationType(requestparam?.id);
      const alertTypeResult = await updateNotificationType({
        ...requestparam,
        version : reloadAlertType?.version
      }, context);
      await Promise.all(triggerNotification?.map(async(triggerOb, index) => {
        const {attribute, operator, placeHolder, triggerDelete}=triggerOb;
        if (triggerDelete) {
          const {id}=triggerOb?.triggerAlert;
          await deleteNotificationTypeTriggers(id);
        } else if (triggerOb?.triggerAlert?.id) {
          const {
            alertType,
            activatedOn,
            createdOn,
            deletedOn,
            groups,
            id,
            lastModifiedOn,
            status,
            version,
            _self,
            name
          } = triggerOb?.triggerAlert;
          const {triggerAttributes} = notificationType;
          const selectedAttr = triggerAttributes.filter((attr) => attr.name === attribute.value);
          const {name: triggerName, placeHolder: triggerPlaceholder} = selectedAttr[0];
          const triggerReq={
            alertType,
            activatedOn,
            createdOn,
            deletedOn,
            groups,
            id,
            lastModifiedOn,
            status,
            version,
            _self,
            name,
            evaluatedTriggerExpression : `${triggerName} ${operator.value} ${placeHolder.value}`,
            placeHolders               : {
              [triggerPlaceholder] : placeHolder.value
            },
            triggerExpression : `${triggerName} ${operator.value} ${'${'+triggerPlaceholder+'}'}`
          };
          const reloadTroggerType = await getTriggerTypes(requestparam?.id, id);
          await updateTriggerNotificationType({...triggerReq, version: reloadTroggerType?.[0]?.version}, context);
        } else {
          const {attribute, operator, placeHolder}=triggerOb;
          const attributeName = attribute.placeHolder;
          const triggerReq={
            alertType    : alertTypeResult[0]?._self,
            name         : `${name+'_Trigger_'+index}`,
            placeHolders : {
              [attributeName] : placeHolder.value
            },
            triggerExpression : `${attribute.name} ${operator.value} ${'${'+attributeName+'}'}`
          };
          await triggerNotificationType(triggerReq, context);
        }
      }));
      dispatch({type: EDIT_NOTIFICATION_TYPE_RESPONSE, notificationType: alertTypeResult});
      dispatch(toggleUserPrompt(successEditOptions(Entity.notificationType)));
    } catch (err) {
      dispatch({type: EDIT_NOTIFICATION_TYPE_ERROR, error: err});
      dispatch(toggleUserPrompt(errorAddOptions(Entity.notificationType, err, true)));
      error(err);
    }
  };
};

/**
 *
 * Invoked when the Delete NotificationType Dialog is to be displayed or removed.
 */
export const openDelete = (notificationType: NotificationType, show = true) => ({
  type : DELETE_NOTIFICATION_TYPE, notificationType, show
});

/**
 *
 * Invoked when an existing notificationType is to be deleted, delegating to the deleteNotification
 * CTC API and dispatching a successful response or error action accordingly.
 */
export const onDeleteNotificationType = ({id}: NotificationType) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: DELETE_NOTIFICATION_TYPE_REQUEST});
    try {
      const deleted = await deleteNotificationType(id);
      dispatch({type: DELETE_NOTIFICATION_TYPE_RESPONSE, notificationType: deleted});
    } catch (err) {
      dispatch({type: DELETE_NOTIFICATION_TYPE_ERROR, error: err});
      error(err);
    }
  };
};

/**
 * Invoked whenever user change the rows per page dropdown
 */
export const setNotificationTypeGridPageSize = (pageSize: number) => ({
  type : SET_GRID_PAGE_SIZE, pageSize
});

/**
 * REDUCERS
 */

export interface NotificationTypeAction extends AdminAction {
  notificationType?: NotificationType;
  searchParams?: NotificationTypeFilters;
};

export interface NotificationTypeAuditState extends NotificationTypeActionState {
  showChangesDialog?: boolean;
  auditRecord?: AuditRecordType;
};

export interface NotificationTypeEditAction extends AdminAction {
  notificationType?: NotificationType;
  isView?: boolean;
};

export interface NotificationTypeActionState extends AsyncState {
  selected: NotificationType | NotificationType[] | null;
  result: NotificationType | NotificationType[] | null;
  isEditing: boolean;
  record?: NotificationType | NotificationType[] | null;
  isView?: boolean;
};

// defines the user preference key for the admin notificationType page ...
export const PREFERENCE = 'notification-type';

export interface NotificationTypeState extends PageState {
  pathname: string;
  isPreInitialized: boolean;
  notificationType: {
    error: ServiceError;
    loading: boolean;
  },
  grid: {
    columnState: ColumnState[],
    preferedColumnState: ColumnState[],
    hasUpdate: boolean,
    pageSize: number
  },
  add: NotificationTypeActionState,
  edit: NotificationTypeActionState,
  audit: NotificationTypeAuditState,
  destroy: NotificationTypeActionState,
  search: NotificationTypeFilters
}

// defines the initial state for the reducer ...
export const initialState: NotificationTypeState = {
  pathname         : null,
  isPreInitialized : false,
  isInitialized    : false,
  notificationType : {
    error   : null,
    loading : false
  },
  grid : {
    columnState         : null,
    preferedColumnState : null,
    hasUpdate           : false,
    pageSize            : gridDefaultPageSize
  },
  add : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null,
    isView    : false
  },
  edit : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null,
    isView    : false
  },
  audit : {
    isEditing         : false,
    selected          : null,
    loading           : false,
    error             : null,
    result            : null,
    showChangesDialog : false,
    auditRecord       : null
  },
  destroy : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null,
    isView    : false
  },
  search : {}
};

// defines the reducers ...
const reducers = {
  [SET_NOTIFICATION_TYPE_PARAMS] : (draft: NotificationTypeState, {pathname}) => {
    draft.pathname    = pathname;
  },
  [INITIALIZE_NOTIFICATION_TYPE] : (draft: NotificationTypeState) => {
    draft.isInitialized            = true;
    const preference               = getGridColumnState(PREFERENCE, true);
    draft.grid.columnState         = preference;
    draft.grid.preferedColumnState = preference;
    draft.destroy.error            = null;
  },
  [SET_GRID_COLUMNS] : ({grid, notificationType}: NotificationTypeState, {columnState}) => {
    onSetGridColumns({
      grid,
      isLoading  : notificationType.loading,
      preference : PREFERENCE
    }, columnState);
  },
  [UPDATE_GRID_COLUMNS] : ({grid}: NotificationTypeState, {columnState}) => {
    onUpdateGridColumns({grid, preference: PREFERENCE}, columnState);
  },
  [NOTIFICATION_TYPE_GRID_REFRESHED] : ({add, edit, destroy}: NotificationTypeState) => {
    add.result = null;
    edit.result = null;
    destroy.result = null;
  },
  [GET_NOTIFICATION_TYPE_REQUEST] : ({notificationType}: NotificationTypeState) => {
    notificationType.loading = true;
  },
  [GET_NOTIFICATION_TYPE_RESPONSE] : ({notificationType}: NotificationTypeState) => {
    notificationType.loading = false;
  },
  [GET_NOTIFICATION_TYPE_ERROR] : ({notificationType}: NotificationTypeState, {error}: NotificationTypeAction) => {
    notificationType.loading = false;
    notificationType.error = error;
  },
  [ADD_NOTIFICATION_TYPE] : ({add}: NotificationTypeState, {show}: NotificationTypeAction) => {
    add.isEditing = show;
    add.result = null;
  },
  [ADD_NOTIFICATION_TYPE_REQUEST] : ({add}: NotificationTypeState) => {
    add.loading = true;
    add.error = add.result = null;
  },
  [ADD_NOTIFICATION_TYPE_RESPONSE] : ({add}: NotificationTypeState, {notificationType}: NotificationTypeAction) => {
    add.loading = false;
    add.isEditing = false;
    add.result = notificationType;
  },
  [ADD_NOTIFICATION_TYPE_ERROR] : ({add}: NotificationTypeState, {error}: NotificationTypeAction) => {
    add.loading = false;
    add.error = error;
  },
  [EDIT_NOTIFICATION_TYPE] : ({edit}: NotificationTypeState,
    {notificationType, show, isView}: NotificationTypeEditAction) => {
    edit.selected      = show ? notificationType : null;
    edit.isEditing     = show;
    edit.isView        = isView;
    edit.result        = null;
  },
  [EDIT_NOTIFICATION_TYPE_REQUEST] : ({edit}: NotificationTypeState) => {
    edit.loading = true;
    edit.error = edit.result = null;
  },
  [EDIT_NOTIFICATION_TYPE_RESPONSE] : ({edit}: NotificationTypeState, {notificationType}: NotificationTypeAction) => {
    edit.loading = false;
    edit.isEditing = false;
    edit.isView = false;
    edit.result = notificationType;
  },
  [EDIT_NOTIFICATION_TYPE_ERROR] : ({edit}: NotificationTypeState, {error}: NotificationTypeAction) => {
    edit.loading = false;
    edit.error = error;
  },
  [AUDIT_NOTIFICATION_TYPE] : ({audit}: NotificationTypeState, {notificationType, show}: NotificationTypeAction) => {
    audit.selected  = show ? notificationType : null;
    audit.isEditing = show;
  },
  [AUDIT_NOTIFICATION_TYPE_SHOW_CHANGES_DIALOG] : ({audit}: NotificationTypeState, {auditRecord, show}) => {
    audit.showChangesDialog = show;
    audit.auditRecord       = auditRecord;
  },
  [DELETE_NOTIFICATION_TYPE] : ({destroy}: NotificationTypeState,
    {notificationType, show}: NotificationTypeAction) => {
    destroy.selected = show ? notificationType : null;
    destroy.isEditing = show;
    destroy.result = null;
  },
  [DELETE_NOTIFICATION_TYPE_REQUEST] : ({destroy}: NotificationTypeState) => {
    destroy.loading = true;
    destroy.error = destroy.result = null;
  },
  [DELETE_NOTIFICATION_TYPE_RESPONSE] : ({destroy}: NotificationTypeState,
    {notificationType}: NotificationTypeAction) => {
    destroy.loading = false;
    destroy.isEditing = false;
    destroy.result = notificationType;
  },
  [DELETE_NOTIFICATION_TYPE_ERROR] : ({destroy}: NotificationTypeState, {error}: NotificationTypeAction) => {
    destroy.loading = false;
    destroy.error = error;
    destroy.result = null;
  },
  [UPDATE_NOTIFICATION_TYPE_SEARCH_QUERIES] : ({search}: NotificationTypeState, {searchParams}) => {
    search.name   = searchParams.name || null;
    search.status = searchParams.status || null;
  },
  [RESET_NOTIFICATION_TYPE_SEARCH_QUERIES] : ({search}: NotificationTypeState) => {
    search.name   = null;
    search.status = null;
  },
  [SET_GRID_PAGE_SIZE] : ({grid}: NotificationTypeState, {pageSize}) => {
    grid.pageSize = pageSize;
  }
};

// defines all reducers for actions of interest to the this reducer ...
export default produce((draft: NotificationTypeState = initialState, action) => {
  reducers?.[action.type]?.(draft, action);
  return draft;
});
